import { GAME_ASSET_SLUG } from "@/config/game";
import { DropdownItem, MultiDropdown } from "@/design-system/Dropdown/Dropdown";
import { ClubData, LeagueData } from "@repo/shared/api";
import { useTranslation } from "@repo/shared/i18n";
import { X } from "lucide-react";
import { ReactNode, useMemo, useRef } from "react";
import { useResizeObserver } from "usehooks-ts";

// Preview pill width plus the gap before it
const PREVIEW_SPACE = 88 + 8;
const MAX_PREVIEW = 3;

const assetsBaseUrl = import.meta.env.VITE_APP_ASSETS_BASE_URL;
const womenSuffix = (isWomen?: boolean) => (isWomen ? " (w)" : "");

// League and club rows, shared by the squad builder's pickers
export const toLeagueItems = (leagues: LeagueData[]): DropdownItem[] =>
  leagues.map((l) => ({
    value: String(l.id),
    label: l.name + womenSuffix(l.isWomen),
    icon: (
      <img
        className="h-4"
        src={`${assetsBaseUrl}/${GAME_ASSET_SLUG}/leagues/dark/${l.id}.png`}
        alt={l.name}
      />
    ),
  }));

export const toClubItems = (clubs: ClubData[]): DropdownItem[] =>
  clubs.map((c) => ({
    value: String(c.id),
    label: c.name + womenSuffix(c.isWomen),
    icon: (
      <img
        className="size-5"
        src={`${assetsBaseUrl}/${GAME_ASSET_SLUG}/clubs/${c.id}.png?scale=50`}
        alt={c.name}
      />
    ),
  }));

type Props = {
  label: string;
  items: DropdownItem[];
  selected: number[];
  // Toggles one id; -1 clears them all
  onSelect: (id: number) => void;
  // Shown in the preview while nothing is picked
  placeholderIcon: ReactNode;
  hideSearch?: boolean;
};

// A multi-select row: the dropdown, and a preview where a click removes a pick
export const FilterDropdown = ({
  label,
  items,
  selected,
  onSelect,
  placeholderIcon,
  hideSearch,
}: Props) => {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  // The panel spans the whole row, so it never hangs out of the side panel
  const { width } = useResizeObserver({ ref, box: "border-box" });

  // Picks first; the panel freezes this order while it's open
  const ordered = useMemo(() => {
    const picked = new Set(selected.map(String));
    return [...items].sort(
      (a, b) => Number(picked.has(b.value)) - Number(picked.has(a.value)),
    );
  }, [items, selected]);

  const icons = selected.map(
    (id) => items.find((item) => item.value === String(id))?.icon,
  );

  return (
    <div ref={ref} className="flex w-full gap-x-2">
      <MultiDropdown
        items={ordered}
        label={label}
        selected={selected.map(String)}
        onChange={(item) => onSelect(Number(item.value))}
        variant="secondary"
        hideSearch={hideSearch}
        width={width}
        triggerWidth={width && width - PREVIEW_SPACE}
      />
      <div className="group relative flex h-8 flex-1 items-center justify-center gap-x-1 rounded-[20px] border border-edge-strong bg-surface text-foreground-faint">
        {icons.length === 0 ? (
          <span className="size-5">{placeholderIcon}</span>
        ) : (
          icons.slice(0, MAX_PREVIEW).map((icon, i) => (
            <button
              key={selected[i]}
              type="button"
              aria-label={t("clear")}
              onClick={() => onSelect(selected[i])}
              className="group/pick relative flex h-6 items-center rounded [&_img]:h-3.5 [&_img]:w-auto"
            >
              {icon}
              <span className="absolute inset-0 hidden items-center justify-center rounded bg-black/60 text-foreground group-hover/pick:flex">
                <X className="size-3.5" />
              </span>
            </button>
          ))
        )}
        {icons.length > 0 && (
          <button
            type="button"
            aria-label={t("clear")}
            onClick={() => onSelect(-1)}
            className="absolute -right-2 -top-2 hidden size-5 items-center justify-center rounded-full border border-edge-strongest bg-control text-foreground-muted hover:text-foreground group-hover:flex"
          >
            <X className="size-3" />
          </button>
        )}
      </div>
    </div>
  );
};
